import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";

import axios from "axios";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("cart");
    return saved ? JSON.parse(saved) : [];
  });

  // Sync cart with localStorage
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // Helper: fetch available quantity from inventory service
  const fetchAvailableQuantity = async (productId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_INVENTORY_SERVICE_URL}/inventories/${productId}/quantity`
      );
      return Number(res.data);
    } catch (err) {
      console.error("Failed to fetch quantity", err);
      return Infinity;
    }
  };

  // Add to cart
  const addToCart = useCallback(async (product, quantity = 1) => {
    const availableQuantity = await fetchAvailableQuantity(product.productId);

    setCart((prev) => {
      const existing = prev.find((p) => p.productId === product.productId);
      if (existing) {
        return prev.map((p) =>
          p.productId === product.productId
            ? {
                ...p,
                quantity: Math.min(p.quantity + quantity, availableQuantity),
                availableQuantity,
              }
            : p
        );
      }
      return [
        ...prev,
        {
          ...product,
          quantity: Math.min(quantity, availableQuantity),
          availableQuantity,
        },
      ];
    });
  }, []);

  // Remove from cart
  const removeFromCart = useCallback((productId) => {
    setCart((prev) => prev.filter((p) => p.productId !== productId));
  }, []);

  // Update quantity
  const updateQuantity = useCallback((productId, quantity) => {
    console.log(quantity);
    setCart((prev) =>
      prev.map((p) =>
        p.productId === productId
          ? {
              ...p,
              quantity: Math.min(
                Math.max(quantity, 1), // never less than 1
                p.availableQuantity ?? Infinity // never more than availableQuantity if present
              ),
            }
          : p
      )
    );
  }, []);

  // Check if product is in cart
  const isInCart = useMemo(
    () => (productId) => cart.some((item) => item.productId === productId),
    [cart]
  );

  // Derived values
  const { totalPrice, cartCount } = useMemo(() => {
    const totalPrice = cart.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    return { totalPrice, cartCount };
  }, [cart]);

  // Memoize context value
  const value = useMemo(
    () => ({
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      totalPrice,
      cartCount,
      isInCart,
    }),
    [
      cart,
      totalPrice,
      cartCount,
      isInCart,
      addToCart,
      removeFromCart,
      updateQuantity,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
